import { useState, useMemo, useEffect } from "react";

export default function SortableTable({ columns, data, renderRow }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const sortedData = useMemo(() => {
    const sorted = [...data];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };
  // Reset pagination when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);
  return (
    <div className="w-full">
      <table className="border border-amber-500 rounded-lg min-w-full bg-white text-sm text-left text-gray-950">
        <thead className="bg-amber-500 text-xs uppercase text-white">
          <tr className="">
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-end py-3 px-2 border-b cursor-pointer select-none"
                onClick={() => handleSort(col.key)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  <span className="text-xs">
                    {sortConfig.key === col.key
                      ? sortConfig.direction === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-300">
          {paginatedData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="font-bold text-md text-center text-gray-950 py-6"
              >
                No match found
              </td>
            </tr>
          ) : (
            paginatedData.map(renderRow)
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 text-sm bg-transparent">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-amber-500 text-white rounded-full disabled:opacity-50 shadow-sm"
          >
            Prev
          </button>
          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-amber-500 text-white rounded-full disabled:opacity-50 shadow-sm"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
